/**
 * Responsibility: fade distant geometry into the background colour.
 *
 * The fog is doing more work than it appears. Beyond softening the look, it is what hides
 * the tile loading boundary: terrain reaches full transparency before it reaches the edge
 * of the loaded area, so new tiles are never seen appearing.
 *
 * That gives a hard constraint. FOG.FAR must stay shorter than the distance to the
 * nearest unloaded tile, and RENDER.FAR_CLIP must stay beyond FOG.FAR or geometry would
 * be clipped while still visible, producing exactly the hard edge the fog exists to
 * prevent.
 *
 * ## The fog has to be radial, and Three.js does not do that by default
 *
 * Three.js measures fog along the **view axis** (`-mvPosition.z`), so the fade boundary is a flat
 * plane. Tile loading measures **distance from the viewer**, so its boundary is a sphere. Those two
 * shapes agree dead ahead and diverge badly toward the screen edges.
 *
 * At a 16:9 aspect and a 60 degree vertical field of view, the horizontal half-angle is about 46
 * degrees. Terrain fully faded at 7,000 units straight ahead is not fully faded at the screen edge
 * until 7,000 / cos(46 degrees), roughly 10,000 units — well past the 7,800 where tiles stop
 * loading. The result is terrain arriving at the left and right edges still about a quarter
 * visible, worse at the corners. That was reported from a test flight as terrain snapping in at the
 * far left and right around the horizon, which is exactly where the geometry puts it.
 *
 * `patchMaterialForRadialFog` replaces the one line that computes fog depth so it uses true
 * distance instead. The fade boundary then becomes a sphere of the same shape as the load boundary,
 * with margin at every angle, and it costs nothing — no extra tiles, no shortened view distance.
 */

import * as THREE from 'three';
import { FOG, RENDER } from '../config.js';

/**
 * The include directive Three.js uses for fog depth, as it appears in the *unresolved* shader.
 *
 * ## Why the directive and not the line inside it
 *
 * `onBeforeCompile` runs **before** Three.js expands `#include` directives — the renderer calls it,
 * and `resolveIncludes` happens later inside `WebGLProgram`. So at patch time the source still says
 * `#include <fog_vertex>`; the `vFogDepth = - mvPosition.z;` inside that chunk is not there yet.
 *
 * The first version of this patch searched for the expanded line, never found it, and threw — which
 * killed the render loop on the first frame and produced a black screen.
 */
const FOG_VERTEX_INCLUDE = '#include <fog_vertex>';

/**
 * Radial replacement for the whole chunk: true distance from the camera rather than depth along its
 * axis.
 *
 * Keeps the `USE_FOG` guard, because `vFogDepth` is only declared when fog is enabled and an
 * unguarded assignment would fail to compile on a material used without scene fog.
 */
const RADIAL_FOG_VERTEX = ['#ifdef USE_FOG', '\tvFogDepth = length( mvPosition.xyz );', '#endif'].join(
  '\n',
);

/**
 * Rewrite a vertex shader's fog calculation to use distance rather than depth.
 *
 * Pure string work, separated from the material so it can be tested against Three.js's own shader
 * source without a WebGL context.
 *
 * @param {string} source - Unresolved vertex shader source, as `onBeforeCompile` receives it.
 * @returns {{source: string, patched: boolean}} `patched` is false when the directive was not found,
 *   which means Three.js has changed and the caller should say so.
 */
export function applyRadialFogToShaderSource(source) {
  if (!source.includes(FOG_VERTEX_INCLUDE)) {
    return { source, patched: false };
  }

  return { source: source.replace(FOG_VERTEX_INCLUDE, RADIAL_FOG_VERTEX), patched: true };
}

/**
 * Make a material's fog radial.
 *
 * Call on every material that participates in fog. Materials with `fog: false` — the aircraft, the
 * depth masks — need no patch.
 *
 * @param {THREE.Material} material
 * @returns {THREE.Material} The same material, for chaining.
 */
export function patchMaterialForRadialFog(material) {
  material.onBeforeCompile = (shader) => {
    const { source, patched } = applyRadialFogToShaderSource(shader.vertexShader);

    if (!patched) {
      /*
        Reported, not thrown. Throwing here aborts the render call and the frame loop never schedules
        another one, so the whole application goes black — which is what happened the first time this
        was written. Losing radial fog costs some terrain visibly arriving at the screen edges; losing
        the render loop costs everything.
      */
      console.error(
        'patchMaterialForRadialFog: could not find "#include <fog_vertex>" in the vertex shader. ' +
          'Three.js has probably changed its shader chunks. Fog is falling back to depth-based, ' +
          'which makes terrain pop in at the screen edges — see render/fog.js.',
      );
      return;
    }

    shader.vertexShader = source;
  };

  /*
    Programs are cached by their generated source plus this key. Without a distinct key, a patched
    material could be handed a cached program compiled from the unpatched source.
  */
  material.customProgramCacheKey = () => 'radial-fog';

  return material;
}

/**
 * Largest angle from the camera axis that appears on screen, in radians.
 *
 * The screen corner, not the edge: that is where the depth-based fog boundary sits furthest away
 * and where pop-in appeared first. Exported so the relationship between fog and load distance can
 * be asserted rather than assumed.
 *
 * @param {number} verticalFovDegrees
 * @param {number} aspect - Width over height.
 * @returns {number} Radians.
 */
export function maxViewAngle(verticalFovDegrees, aspect) {
  const halfVertical = Math.tan((verticalFovDegrees / 2) * (Math.PI / 180));
  const halfHorizontal = halfVertical * aspect;
  return Math.atan(Math.hypot(halfVertical, halfHorizontal));
}

/**
 * Attach linear fog to a scene.
 *
 * Linear rather than exponential: linear fog has an exact end distance, which is what
 * lets us guarantee terrain is fully faded before the loaded area runs out. Exponential
 * fog only approaches zero asymptotically, so faint lines would linger past the boundary.
 *
 * @param {THREE.Scene} scene
 * @returns {THREE.Fog} The fog instance, so callers can retune it at runtime.
 */
export function applyFog(scene) {
  if (FOG.FAR >= RENDER.FAR_CLIP) {
    throw new Error(
      'applyFog: FOG.FAR must be closer than RENDER.FAR_CLIP, or terrain is clipped before it fades',
    );
  }
  if (FOG.NEAR >= FOG.FAR) {
    throw new Error('applyFog: FOG.NEAR must be closer than FOG.FAR');
  }

  scene.fog = new THREE.Fog(RENDER.BACKGROUND_COLOR, FOG.NEAR, FOG.FAR);
  return scene.fog;
}

/**
 * Retune the fade distances at runtime, for the look-tuning pass.
 *
 * @param {THREE.Scene} scene
 * @param {number} near
 * @param {number} far
 */
export function setFogRange(scene, near, far) {
  if (!scene.fog) {
    throw new Error('setFogRange: scene has no fog; call applyFog first');
  }
  scene.fog.near = near;
  scene.fog.far = far;
}
